import * as React from 'react';
import { Card, notify } from 'sha-el-design/lib';
import { WorkHistoryCall, CallStatusEnum } from '../../../schema';
import { FaCheck, FaPhoneSlash, FaExclamationTriangle, FaBan } from 'react-icons/fa';
import './callingStyles';

export class ActiveCall extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      callStatus: null,
    };
  }

  // PROJECT-ANALYSIS section 9: "No keyboard shortcuts in a screen whose users
  // repeat the same 4 buttons hundreds of times a day." 1-4 map to the four
  // outcomes, in the order they appear. Ignored while typing so the
  // questionnaire and Edit Retailer forms are unaffected.
  componentDidMount() {
    document.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
  }

  onKeyDown = (e: KeyboardEvent) => {
    const el = document.activeElement as HTMLElement;
    if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) {
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }
    const outcome = {
      1: CallStatusEnum.NO_REPLY,
      2: CallStatusEnum.WRONG_NUMBER,
      3: CallStatusEnum.ANSWERED,
      4: CallStatusEnum.REJECTED,
    }[e.key];
    if (!outcome) {
      return;
    }
    // Never let a shortcut do what the equivalent button would refuse to do.
    if (outcome === CallStatusEnum.REJECTED && !this.canReject()) {
      return;
    }
    if (outcome !== CallStatusEnum.REJECTED && this.isOutcomeDisabled()) {
      return;
    }
    e.preventDefault();
    this.updateCallStatus(outcome);
  }

  /** Unchanged from the original inline expression, extracted so the three
   *  buttons and the shortcut handler cannot drift apart. */
  isOutcomeDisabled = () => {
    const call = this.props.call;
    return (!call.retailer.mobilenumber2 || (call.mobileNumber === call.retailer.mobilenumber2)) &&
      (call.callStatus && (call.callStatus !== CallStatusEnum.NO_REPLY));
  }

  canReject = () => {
    const call = this.props.call;
    return call.attempts > 2
      && !((call.attempts < 2) || (call.callStatus === CallStatusEnum.REJECTED));
  }

  updateCallStatus = (callStatus: CallStatusEnum) => {
    const { isAlternateNumberActive } = this.props;

    this.props.deactivateAlternateNumber();
    this.setState({ callStatus });
    switch (callStatus) {
      case CallStatusEnum.NO_REPLY: {
        if (isAlternateNumberActive || !this.props.call.retailer.mobilenumber2) {
          this.setState({ callStatus: null });
          return notify({
            type: 'info',
            title: 'Moving to next Retailer',
            message: 'Alternate number not available',
            callBack: () => this.props.onNoAnswer(isAlternateNumberActive),
          });
        }
        return notify({
          type: 'info',
          title: 'Activating Alternate Number',
          message: 'Please call Again using alternate number',
          duration: 3000,
          callBack: () => this.setState({ callStatus: null }, this.props.activateAlternateNumber),
        });
      }
      case CallStatusEnum.WRONG_NUMBER: {
        if (isAlternateNumberActive || !this.props.call.retailer.mobilenumber2) {
          this.setState({ callStatus: null });
          return notify({
            type: 'info',
            title: 'Moving to next Retailer',
            message: 'Alternate number not available',
            callBack: () => this.props.onWrongNumber(isAlternateNumberActive),
          });
        }
        return notify({
          type: 'info',
          title: 'Activating Alternate Number',
          message: 'Please call Again using alternate number',
          duration: 3000,
          callBack: () => {
            this.setState({ callStatus: null }, () => {
              this.props.activateAlternateNumber();
              this.props.onWrongNumber(isAlternateNumberActive);
            });
          },
        });
      }
      case CallStatusEnum.REJECTED: {
        return notify({
          type: 'info',
          title: 'Retailer Rejected',
          message: 'Moving to next Retailer',
          callBack: () => this.props.onReject(),
        });
      }
      case CallStatusEnum.ANSWERED: {
        this.setState({ callStatus: null });
        this.props.onAnswer(isAlternateNumberActive);
      }
    }
  }

  render() {
    const call = this.props.call;
    const disabled = this.isOutcomeDisabled();
    // Colours were magic 3-char hexes (#23c/#c32/#3c2/#ccc) set inline; widths
    // were percentage strings that had to change with the button count. Both
    // now come from callingStyles, and the grid handles 3 or 4 buttons.
    return (
      <div className='cs-call'>
        <Card title='Record the outcome'>
          <div className='cs-call__outcomes'>
            <button
              type='button'
              className='cs-call__btn cs-call__btn--noreply'
              onClick={() => this.updateCallStatus(CallStatusEnum.NO_REPLY)}
              disabled={disabled}
            >
              <span className='cs-call__key'>1</span>
              <FaPhoneSlash />
              No Answer
            </button>
            <button
              type='button'
              className='cs-call__btn cs-call__btn--wrong'
              onClick={() => this.updateCallStatus(CallStatusEnum.WRONG_NUMBER)}
              disabled={disabled}
            >
              <span className='cs-call__key'>2</span>
              <FaExclamationTriangle />
              Wrong Number
            </button>
            <button
              type='button'
              className='cs-call__btn cs-call__btn--answered'
              onClick={() => this.updateCallStatus(CallStatusEnum.ANSWERED)}
              disabled={disabled}
            >
              <span className='cs-call__key'>3</span>
              <FaCheck />
              Answered
            </button>
            {call.attempts > 2 && (
              <button
                type='button'
                className='cs-call__btn cs-call__btn--reject'
                disabled={(call.attempts < 2) || (call.callStatus === CallStatusEnum.REJECTED)}
                onClick={() => this.updateCallStatus(CallStatusEnum.REJECTED)}
              >
                <span className='cs-call__key'>4</span>
                <FaBan />
                Reject Retailer
              </button>
            )}
          </div>
          <p className='cs-call__hint'>
            Shortcuts <kbd>1</kbd> No Answer <kbd>2</kbd> Wrong Number <kbd>3</kbd> Answered
            {call.attempts > 2 ? <> <kbd>4</kbd> Reject</> : null}
          </p>
        </Card>
      </div>
    );
  }
}

interface Props {
  call: WorkHistoryCall;
  isAlternateNumberActive: boolean;
  onNoAnswer: (isAlternateNumberActive: boolean) => void;
  onAnswer: (isAlternateNumberActive: boolean) => void;
  onWrongNumber: (isAlternateNumberActive: boolean) => void;
  onReject: () => void;
  activateAlternateNumber: () => void;
  deactivateAlternateNumber: () => void;
}

interface State {
  callStatus: CallStatusEnum;
}
